// src/components/CookieConsent.tsx
// A lightweight cookie/analytics consent banner. The site's own state lives in
// localStorage (cart, auth, catalog cache) — no cookies — so the non-essential
// tracking is Google Analytics (_ga/_ga_*) plus the third parties we embed, and
// none of it may load until the visitor accepts. We therefore keep
// initAnalytics() OUT of index.tsx and only call it here on accept (or
// immediately for a returning visitor who already accepted). Declining simply
// never initializes GA.
//
// The stored choice itself lives in lib/consent.ts — this component owns the
// banner UI, not the state. Other gated embeds read the same store.

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { initAnalytics } from "../lib/analytics";
import { initMetaPixel } from "../lib/meta-pixel";
import { pingVisit } from "../lib/attribution";
import {
  isGpcEnabled,
  readConsent,
  subscribeConsent,
  writeConsent,
} from "../lib/consent";

const CookieConsent: React.FC = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const evaluate = () => {
      // GPC, when present, is a valid opt-out — never load analytics, never
      // prompt. It overrides any stored choice.
      if (isGpcEnabled()) {
        setVisible(false);
        return;
      }

      const choice = readConsent();
      if (choice === "accepted") {
        // Returning visitor who already opted in — load analytics without asking.
        initAnalytics();
        initMetaPixel();
        setVisible(false);
      } else {
        // No decision yet — prompt. Declined visitors see nothing and get no GA.
        setVisible(choice !== "declined");
      }
    };

    evaluate();
    // Re-evaluate when the choice is cleared from "Manage cookie preferences"
    // or changed in another tab, so the banner reappears without a reload.
    return subscribeConsent(evaluate);
  }, []);

  if (!visible) return null;

  const accept = () => {
    writeConsent("accepted");
    initAnalytics();
    initMetaPixel();
    // This visit was already captured at app start but couldn't be counted
    // until now — ping it, or a first-time visitor who accepts is missing from
    // the Traffic tab's visit totals for their whole first session.
    pingVisit();
    setVisible(false);
  };

  const decline = () => {
    writeConsent("declined");
    setVisible(false);
  };

  return (
    <div
      className="cookie-consent"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
    >
      <p className="cookie-consent-text">
        We use a few cookies to understand how the site is used and improve your
        experience. Essential features work without them. See our{" "}
        <Link to="/privacy" className="cookie-consent-link">
          Privacy Policy
        </Link>
        .
      </p>
      <div className="cookie-consent-actions">
        <button
          type="button"
          className="cookie-consent-btn cookie-consent-btn--secondary"
          onClick={decline}
        >
          Decline
        </button>
        <button
          type="button"
          className="cookie-consent-btn cookie-consent-btn--primary"
          onClick={accept}
        >
          Accept
        </button>
      </div>
    </div>
  );
};

export default CookieConsent;
